import React, { useCallback } from 'react';
import { Slot } from "@radix-ui/react-slot";
import { cva } from "class-variance-authority";
import { cn } from "../../utils/cn";
import * as LucideIcons from 'lucide-react';

const buttonVariants = cva(
  "inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium transition-all duration-200 ease-in-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 cursor-pointer select-none relative overflow-hidden",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary/90 shadow-sm hover:shadow-md active:scale-[0.98]",
        destructive: "bg-error text-error-foreground hover:bg-error/90 shadow-sm hover:shadow-md active:scale-[0.98]",
        outline: "border border-border bg-background hover:bg-accent hover:text-accent-foreground shadow-sm hover:shadow-md active:scale-[0.98]",
        secondary: "bg-secondary text-secondary-foreground hover:bg-secondary/80 shadow-sm hover:shadow-md active:scale-[0.98]",
        ghost: "hover:bg-accent hover:text-accent-foreground active:scale-[0.98]",
        link: "text-primary underline-offset-4 hover:underline active:scale-[0.98]",
        success: "bg-success text-success-foreground hover:bg-success/90 shadow-sm hover:shadow-md active:scale-[0.98]",
        warning: "bg-warning text-warning-foreground hover:bg-warning/90 shadow-sm hover:shadow-md active:scale-[0.98]",
        danger: "bg-error text-error-foreground hover:bg-error/90 shadow-sm hover:shadow-md active:scale-[0.98]",
      },
      size: {
        default: "h-10 px-4 py-2 min-w-[80px]",
        sm: "h-9 rounded-md px-3 text-sm min-w-[70px]",
        lg: "h-11 rounded-md px-8 text-base min-w-[100px]",
        icon: "h-10 w-10 p-0",
        xs: "h-8 rounded-md px-2 text-xs min-w-[60px]",
        xl: "h-12 rounded-md px-10 text-base min-w-[120px]",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
);

const Button = React.forwardRef(({
  className,
  variant,
  size,
  asChild = false,
  children,
  loading = false,
  iconName = null,
  iconPosition = 'left',
  iconSize = null,
  fullWidth = false,
  disabled = false,
  onClick,
  type = "button",
  ...props
}, ref) => {
  const Comp = asChild ? Slot : "button";

  // Icon size mapping based on button size
  const iconSizeMap = {
    xs: 14,
    sm: 16,
    default: 18,
    lg: 20,
    xl: 22,
    icon: 18,
  };

  const calculatedIconSize = iconSize || iconSizeMap?.[size] || 18;

  // Enhanced loading spinner with proper sizing
  const LoadingSpinner = () => {
    const spinnerSizes = {
      xs: 'h-3 w-3',
      sm: 'h-4 w-4',
      default: 'h-4 w-4',
      lg: 'h-5 w-5',
      xl: 'h-5 w-5',
      icon: 'h-4 w-4'
    };

    return (
      <svg 
        className={cn("animate-spin mr-2", spinnerSizes?.[size] || 'h-4 w-4')} 
        fill="none" 
        viewBox="0 0 24 24"
        role="status"
        aria-label="Loading"
      >
        <circle 
          className="opacity-25" 
          cx="12" 
          cy="12" 
          r="10" 
          stroke="currentColor" 
          strokeWidth="4" 
        />
        <path 
          className="opacity-75" 
          fill="currentColor" 
          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" 
        />
      </svg>
    );
  };

  const renderIcon = () => {
    if (!iconName || loading) return null;
    
    try {
      // Use Lucide React icons directly
      const IconComponent = LucideIcons?.[iconName];
      
      if (IconComponent) {
        return (
          <IconComponent
            size={calculatedIconSize}
            className={cn(
              "flex-shrink-0",
              children && iconPosition === 'left' && "mr-2",
              children && iconPosition === 'right' && "ml-2"
            )}
            aria-hidden="true"
          />
        );
      } else {
        console.warn(`Icon "${iconName}" not found in Lucide React`);
        return null;
      }
    } catch (error) {
      console.warn(`Icon "${iconName}" error:`, error);
      return null;
    }
  };

  // Enhanced click handler with event validation
  const handleClick = React.useCallback((event) => {
    if (disabled || loading) {
      event?.preventDefault();
      event?.stopPropagation();
      return;
    }

    // Ensure the event is properly handled
    if (onClick && typeof onClick === 'function') {
      try {
        onClick(event);
      } catch (error) {
        console.error('Button onClick handler error:', error);
      }
    }
  }, [disabled, loading, onClick]);

  // Fallback button for error scenarios
  const renderFallbackButton = () => (
    <button
      ref={ref}
      type={type}
      className={cn(
        buttonVariants({ variant, size }),
        fullWidth && "w-full",
        loading && "pointer-events-none",
        className
      )}
      disabled={disabled || loading}
      onClick={handleClick}
      aria-disabled={disabled || loading}
      {...props}
    >
      {loading && <LoadingSpinner />}
      {!loading && iconName && iconPosition === 'left' && renderIcon()}
      <span className={cn(loading && "opacity-70")}>{children}</span>
      {!loading && iconName && iconPosition === 'right' && renderIcon()}
    </button>
  );

  // Handle asChild prop for compound components
  if (asChild) {
    try {
      if (!children || React.Children?.count(children) !== 1) {
        return renderFallbackButton();
      }

      const child = React.Children?.only(children);

      if (!React.isValidElement(child)) {
        return renderFallbackButton();
      }

      const content = (
        <>
          {loading && <LoadingSpinner />}
          {!loading && iconName && iconPosition === 'left' && renderIcon()}
          <span className={cn(loading && "opacity-70")}>
            {child?.props?.children}
          </span>
          {!loading && iconName && iconPosition === 'right' && renderIcon()}
        </>
      );

      return React.cloneElement(child, {
        ref,
        className: cn(
          buttonVariants({ variant, size }),
          fullWidth && "w-full",
          loading && "pointer-events-none",
          className,
          child?.props?.className
        ),
        disabled: disabled || loading || child?.props?.disabled,
        onClick: handleClick,
        'aria-disabled': disabled || loading,
        children: content,
        ...props,
      });
    } catch (error) {
      console.warn('Button asChild error:', error);
      return renderFallbackButton();
    }
  }

  return (
    <Comp
      ref={ref}
      type={type}
      className={cn(
        buttonVariants({ variant, size }),
        fullWidth && "w-full",
        loading && "pointer-events-none",
        className
      )}
      disabled={disabled || loading}
      onClick={handleClick}
      aria-disabled={disabled || loading}
      {...props}
    >
      {loading && <LoadingSpinner />}
      {!loading && iconName && iconPosition === 'left' && renderIcon()}
      <span className={cn(loading && "opacity-70")}>{children}</span>
      {!loading && iconName && iconPosition === 'right' && renderIcon()}
    </Comp>
  );
});

Button.displayName = "Button";

export default Button;